/**
 * TaleAroo requisites.
 *
 * The merchant of record is an external service: it is the seller under the contract with the
 * buyer, issues the invoice, and pays sales taxes. That is why there are no tax identifiers
 * here like the Russian brand has: under this scheme they belong to the reseller, not to us.
 *
 * The documents name Polar as that reseller, and so does `reseller` below: it is what a buyer
 * sees as the seller on the `/legal` page. It changed from a placeholder on 28 September 2026,
 * when Polar was connected; no document text changed with it, so the edition did not move.
 *
 * The texts have not been reviewed by a lawyer. See `documents.tsx`.
 */
export const talearooLegal = {
    documentVersion: 'October 4, 2026',
    /**
     * Machine-readable edition identifier. The clickwrap stores it, so the record can say which
     * text a person actually agreed to rather than only when.
     *
     * Bumped on 20 September 2026, when the terms stopped promising deadlines of their own and the
     * governing-law clause stopped granting a forum. The label had been September 4 while the
     * documents kept changing under it, which is the one thing an edition identifier must not do —
     * a date that does not match the text it names.
     *
     * Bumped again on 24 September 2026, when the hosting was chosen: the privacy policy, the photo
     * consent and the voice consent now name OVHcloud, Cloudflare and Resend, and say that stored
     * files may leave Canada.
     *
     * And on 25 September 2026, when the privacy policy began to say how long encrypted backups
     * keep a deleted account's information.
     *
     * And on 2 October 2026, when the photo consent stopped speaking only of "your child": a
     * book's characters can be adults, and the consent now covers everyone in the photographs
     * (the checkbox already said so). The site has not opened, so no customer has accepted an
     * earlier edition; LAST_MATERIAL_NOTICE stays unset — there is no one to write to.
     *
     * And on 4 October 2026, when the privacy policy and the photo consent began to say that
     * photographs of favorite things and places have no approval step and are kept until 30 days
     * after the last work on the book (#74). Still before launch: no notice owed.
     */
    documentVersionId: '2026-10-04',
    serviceName: 'TaleAroo',
    /**
     * The name the sole proprietorship trades under in British Columbia. The footer
     * carries it rather than Polar: Polar's terms (§9.2) require the site to make its reseller role
     * clear but do not say where, and `/legal` and the documents already say it.
     */
    businessName: 'TaleAroo Books',
    /**
     * Polar's legal entity as Polar publishes it: the checkout buyer terms
     * (https://polar.sh/legal/checkout-buyer-terms), the privacy policy
     * (https://polar.sh/legal/privacy-policy) and the master services terms
     * (https://polar.sh/legal/master-services-terms), read on 28 September 2026.
     */
    reseller: {
        name: 'Polar Software, Inc.',
        address: '3500 South DuPont Highway, Dover, DE 19901, United States',
    },
    /**
     * How long the destruction record is kept. Published here and enforced by
     * `DESTRUCTION_RECORD_RETENTION_YEARS` in the worker — two places, one number, exactly like
     * `purgeTerm` and `PURGE_QUARANTINE_DAYS` on the Russian side. Changing one without the other
     * turns the policy into a statement about a period nothing keeps.
     */
    destructionRecordTerm: 'three years',
    /**
     * The quarantine before a closed account's files are destroyed. Published here and enforced
     * by `PURGE_QUARANTINE_DAYS` in the api and the worker — the same "two places, one number"
     * as `destructionRecordTerm`, and the reason the sentence is interpolated rather than
     * written out: a deployment that changed the period would otherwise publish a lie.
     */
    quarantineTerm: 'seven days',
    /**
     * How long a database backup lives before its bucket deletes it. Published here and enforced
     * by the backup bucket's lifecycle rule, `storage.backupRetentionDays` in the platform stack —
     * two places, one number, like the terms above. A deleted account's rows survive in backups
     * for this long, and the privacy policy has to say so.
     */
    backupRetentionTerm: '30 days',
    support: {
        email: 'support@talearoo.com',
    },
    /** The brand's own accounts: the label is what the footer shows. */
    socials: {
        instagram: { label: '@talearoo', href: 'https://www.instagram.com/talearoo' },
        facebook: {
            label: 'TaleAroo',
            href: 'https://www.facebook.com/profile.php?id=61595226216128',
        },
        tiktok: { label: '@talearoo', href: 'https://www.tiktok.com/@talearoo' },
        youtube: { label: '@TaleArooBooks', href: 'https://www.youtube.com/@TaleArooBooks' },
    },
    /**
     * Contact of the person responsible for privacy. Quebec Law 25 requires publishing it, and
     * it is the address a data request goes to.
     */
    privacy: {
        email: 'privacy@talearoo.com',
    },
    /**
     * Reports of abuse. This address is on the public book page, and it is the channel through
     * which a report reaches us at all: the duty to report is reactive, so with no input it
     * never fires.
     */
    abuse: {
        email: 'abuse@talearoo.com',
    },
};
//# sourceMappingURL=legal-data.js.map